import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  ActionType,
  Comment,
  CommentForContest,
  ContestManagement,
  EventPayload,
  EventPayloadDisc,
  LiveEvent,
  LiveEventBus,
} from "../generated/core-types";
import { ServerConfig, fullWSURL } from "../serverConfig";
import { ServerStatus } from "../serverStatus";
import { narrowPayload, sameComment } from "../core";
import { PillTabs } from "../components/PillTabs";
import { JournalTimeline } from "../components/JournalTimeline";
import { CommentsToValidate } from "./CommentsToValidate";
import { ContestValidation } from "./ContestValidation";

type TabValue = "comments" | "contest" | "journal";

interface CommentsValidationProps {
  serverConfig: ServerConfig;
  serverStatus: ServerStatus;
  contestSwitch: (contest: ContestManagement) => void;
  onContestMatchCount: (n: number) => void;
}

function unwrapComment(payload: EventPayload): Comment | null {
  return narrowPayload(payload, EventPayloadDisc.CommentPayload)?.comment ?? null;
}

function unwrapCommentForContest(payload: EventPayload): CommentForContest | null {
  return (
    narrowPayload(payload, EventPayloadDisc.CommentForContestPayload)?.commentForContest ?? null
  );
}

function unwrapContest(payload: EventPayload): ContestManagement | null {
  return narrowPayload(payload, EventPayloadDisc.ContestPayload)?.contest ?? null;
}

export function CommentsValidation(props: CommentsValidationProps): JSX.Element {
  const [commentsList, setCommentsList] = useState<Comment[]>([]);
  const [contestComments, setContestComments] = useState<CommentForContest[]>([]);
  const [tab, setTab] = useState<TabValue>("comments");
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    props.onContestMatchCount(contestComments.length);
  }, [contestComments.length, props]);

  useEffect(() => {
    const ws = new WebSocket(`${fullWSURL(props.serverConfig)}/live/comments/validation`);
    ws.onmessage = (m) => {
      const events = JSON.parse(m.data as string) as LiveEvent[];
      events.forEach((event) => {
        switch (event.eventType) {
          case LiveEventBus.commentToValidate: {
            const c = unwrapComment(event.payload);
            if (c) setCommentsList((prev) => [...prev, c]);
            break;
          }
          case LiveEventBus.commentValidated: {
            const c = unwrapComment(event.payload);
            if (c) setCommentsList((prev) => prev.filter((p) => !sameComment(p, c)));
            break;
          }
          case LiveEventBus.contestSwitch: {
            const contest = unwrapContest(event.payload);
            if (contest) props.contestSwitch(contest);
            setContestComments([]);
            break;
          }
          case LiveEventBus.contestComment: {
            const cfc = unwrapCommentForContest(event.payload);
            if (cfc) setContestComments((prev) => [...prev, cfc]);
            break;
          }
          default:
            console.warn("Received unsupported event:", event);
        }
      });
    };
    wsRef.current = ws;
    return () => {
      ws.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateComment = (comment: Comment, action: ActionType) => {
    const ws = wsRef.current;
    if (ws == null) {
      console.warn("No Websocket, cannot send update");
    } else {
      const updated: Comment = { ...comment, action };
      const event: LiveEvent = {
        eventType: LiveEventBus.commentValidated,
        payload: {
          type: EventPayloadDisc.CommentPayload,
          comment: updated,
        },
      };
      ws.send(JSON.stringify(event));
    }
    setCommentsList((prev) => prev.filter((p) => !sameComment(p, comment)));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <PillTabs<TabValue>
          value={tab}
          onChange={setTab}
          items={[
            { value: "comments", label: "Bình luận", badge: commentsList.length },
            { value: "contest", label: "Cuộc thi", badge: contestComments.length },
            { value: "journal", label: "Nhật ký", badge: props.serverStatus.logs.length },
          ]}
        />
      </Box>
      {tab === "comments" && (
        <CommentsToValidate
          serverConfig={props.serverConfig}
          comments={commentsList}
          onValidate={validateComment}
        />
      )}
      {tab === "contest" && <ContestValidation comments={contestComments} />}
      {tab === "journal" && <JournalTimeline logs={props.serverStatus.logs} />}
    </Box>
  );
}
